/**
 * Embroidery Customization Component
 * Handles character counting and pre-fill from cart for embroidery text input
 */

// Embroidery product handle
window.EMBROIDERY_PRODUCT_HANDLE = window.EMBROIDERY_PRODUCT_HANDLE || 'brodeeraus';

// Initialize character counters on page load
document.addEventListener('DOMContentLoaded', initializeEmbroideryCustomization);

// Re-initialize after dynamic content loads (e.g., variant changes)
document.addEventListener('shopify:section:load', initializeEmbroideryCustomization);

async function initializeEmbroideryCustomization() {
  const embroideryBlocks = document.querySelectorAll('[data-embroidery-block]');

  // Use for...of to properly await async operations
  for (const block of embroideryBlocks) {
    const input = block.querySelector('[data-embroidery-input]');
    const charCountDisplay = block.querySelector('[data-char-count]');
    const counter = block.querySelector('[data-character-counter]');

    if (!input || !charCountDisplay) {
      continue;
    }

    const maxLength = parseInt(input.getAttribute('data-max-length')) || 12;
    const warningThreshold = 10; // Warning at 10 characters (83%)

    // Remove existing listeners by cloning
    const newInput = input.cloneNode(true);
    input.parentNode.replaceChild(newInput, input);

    // Character counter update function
    function updateCharacterCount() {
      const length = newInput.value.length;
      charCountDisplay.textContent = length;

      // Add warning class when approaching limit
      if (counter) {
        if (length >= warningThreshold) {
          counter.classList.add('warning');
        } else {
          counter.classList.remove('warning');
        }
      }
    }

    // Attach event listeners
    newInput.addEventListener('input', updateCharacterCount);
    newInput.addEventListener('keyup', updateCharacterCount);

    // Pre-fill from cart if on embroidery product page
    await preFillFromCart(newInput);

    // Initial count (after pre-fill)
    updateCharacterCount();
  }
}

/**
 * Pre-fill embroidery text input from cart if user is on embroidery product page
 * @param {HTMLInputElement} input - Embroidery text input element
 */
async function preFillFromCart(input) {
  try {
    // Check if we're on the embroidery product page by checking URL path
    const isEmbroideryPage = window.location.pathname.includes(`/products/${window.EMBROIDERY_PRODUCT_HANDLE}`);

    // Only proceed if on embroidery product page
    if (!isEmbroideryPage) {
      return;
    }

    // Fetch current cart to check for existing embroidery text
    const response = await fetch('/cart.js');
    if (!response.ok) {
      return;
    }

    const cart = await response.json();

    // Find embroidery line item by product handle
    const embroideryItem = cart.items.find(item =>
      item.handle === window.EMBROIDERY_PRODUCT_HANDLE
    );

    if (!embroideryItem) {
      return;
    }

    // Extract existing embroidery text from properties
    const existingText = embroideryItem.properties?.['Brodeeraus teksti'] || '';

    if (existingText && existingText.trim() !== '') {
      // Pre-fill input with existing text
      input.value = existingText;

      // Announce to screen readers (accessibility)
      const block = input.closest('[data-embroidery-block]');
      const messageTemplate = block?.dataset?.prefilledMessage || 'Embroidery text prefilled: [count] characters';
      announceToScreenReader(messageTemplate.replace('[count]', existingText.length));
    }

  } catch (error) {
    // Fail silently - pre-fill is a nice-to-have, not critical
    console.error('[Embroidery] Error pre-filling embroidery text from cart:', error);
  }
}

/**
 * Announce message to screen readers via ARIA live region
 * @param {string} message - Message to announce
 */
function announceToScreenReader(message) {
  const liveRegion = document.createElement('div');
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.className = 'visually-hidden';
  liveRegion.textContent = message;

  document.body.appendChild(liveRegion);

  // Remove after announcement (1 second is enough for screen readers)
  setTimeout(() => {
    document.body.removeChild(liveRegion);
  }, 1000);
}
